import type React from "react";

import { faComment } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Notification } from "shared/atoms/Notification";
import { SkeletonLayout } from "shared/atoms/SkeletonLayout";
import { DARK_BLUE } from "theme/colors";

interface MessagesContainerProps {
  children: React.ReactNode;
  isEmpty: boolean;
  isError: boolean;
  isLoading: boolean;
}

export const MessagesContainer: React.FC<MessagesContainerProps> = ({ children, isEmpty, isError, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <SkeletonLayout
        layout={{
          type: "column",
          padding: 3,
          spacing: 2,
          children: [
            {
              type: "skeleton",
              variant: "rounded",
              width: "100%",
              height: 75,
              sx: { maxWidth: "100%", marginLeft: "auto !important" },
            },
            {
              type: "skeleton",
              variant: "rounded",
              width: 375,
              height: 75,
              sx: { maxWidth: "100%", marginLeft: "auto !important" },
            },
            {
              type: "skeleton",
              variant: "rounded",
              width: 375,
              height: 75,
              sx: { maxWidth: "100%", marginLeft: "auto !important" },
            },
            {
              type: "skeleton",
              variant: "rounded",
              width: 375,
              height: 75,
              sx: { maxWidth: "100%", marginRight: "auto !important" },
            },
          ],
        }}
      />
    );
  }

  if (isError) {
    return (
      <Container>
        <Notification variant="danger">{t("errors.generic")}</Notification>
      </Container>
    );
  }

  if (isEmpty) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" spacing={2}>
        <FontAwesomeIcon icon={faComment} fontSize="40px" color={DARK_BLUE[30]} />
        <Typography variant="subtitle1" color={DARK_BLUE[40]}>
          {t("messages.no_messages")}
        </Typography>
      </Stack>
    );
  }

  return <>{children}</>;
};

const Container = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
